// Libs
import { Button, Form, Input, Toast } from 'antd-mobile';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ExclamationCircleOutline,
  EyeInvisibleOutline,
  EyeOutline,
  LockOutline,
  UserOutline,
} from 'antd-mobile-icons';

// Constants
import { PAGE_URL } from '@app/constants/route';

// Reducers
import { ownerApiSlice, useOwnerLoginMutation } from '@app/slice/ownerApiSlice';
import { reportsApiSlice } from '@app/slice/reportsApiSlice';

// Utils
import {
  getLoginErrorMessage,
  LOGIN_STATUS_NO_TOKEN,
  resolveLoginError,
} from '@app/utils/loginError';
import {
  consumeSessionNotice,
  saveOwnerSession,
  SESSION_NOTICE,
} from '@app/utils/ownerAuth';

// Assets — logo DiOwner ĐẦY ĐỦ (mark + chữ "DiOwner" + 3 gạch), bản 512px cho web.
// (Chốt với user 22/08: bố cục brand NHƯ CŨ — logo nhỏ góc trái + text bên cạnh,
// nhưng ảnh logo là bản ĐẦY ĐỦ có chữ, không phải mark trơ.)
import diownerLogo from '@app/assets/png/diowner-logo.png';

// Styles
import './Login.scss';

/**
 * Ô nhập của màn login: icon trái + (tuỳ chọn) nút bên phải.
 * Form.Item truyền `value`/`onChange` xuống component con duy nhất → ở đây ta nhận
 * rồi chuyển thẳng cho Input của antd-mobile, phần vỏ chỉ để trang trí/token theme.
 *
 * Round 17: bàn phím điện thoại tự viết hoa / tự sửa từ làm sai mật khẩu (nhất là
 * khi bật con mắt → type="text"). Tắt cả 3 ở MỌI trạng thái type. antd-mobile Input
 * chuyển autoCapitalize/autoCorrect xuống <input> nhưng KHÔNG chuyển spellCheck →
 * gắn thẳng thuộc tính lên phần tử gốc qua ref (đổi type không tạo lại <input>, nhưng
 * vẫn gắn lại sau mỗi render cho chắc).
 */
const LoginField = ({ prefixIcon, suffix, ...inputProps }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current?.nativeElement;
    if (el && el.getAttribute('spellcheck') !== 'false') {
      el.setAttribute('spellcheck', 'false');
    }
  });

  return (
    <div className="login-field">
      <span className="login-field-icon" aria-hidden="true">
        {prefixIcon}
      </span>
      <Input
        {...inputProps}
        ref={inputRef}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="login-field-input"
      />
      {suffix ? <span className="login-field-suffix">{suffix}</span> : null}
    </div>
  );
};

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const [ownerLogin, { isLoading }] = useOwnerLoginMutation();
  const navigate = useNavigate();

  // Nút con mắt: CHỈ đổi type của ô nhập (password ↔ text), không đụng giá trị.
  const [showPassword, setShowPassword] = useState(false);
  // Thông báo lỗi hiển thị TẠI CHỖ (dải cảnh báo trong thẻ đăng nhập), không phải
  // alert/toast thô — chủ đầu tư đọc được lý do ngay cạnh ô vừa nhập.
  const [loginError, setLoginError] = useState('');

  // Báo lý do vừa bị đưa về màn login. Hai nguồn vì hai kiểu điều hướng:
  //  · location.state — PrivateRoute chặn bằng <Navigate> (điều hướng trong SPA);
  //  · sessionStorage — forceLogout reload cả trang (401 / hết giờ / bấm Đăng xuất),
  //    reload làm mất location.state nên phải gửi cờ qua sessionStorage.
  useEffect(() => {
    const notice = consumeSessionNotice();
    const expiredByState = location?.state?.tokenExpired === true;

    if (expiredByState) {
      // Dọn state để F5 hoặc quay lại màn này không hiện thông báo lần nữa.
      navigate(location.pathname, { replace: true, state: null });
    }

    if (expiredByState || notice === SESSION_NOTICE.EXPIRED) {
      // Hết phiên là LỖI → dùng dải cảnh báo tại chỗ của bản 20/08, đồng bộ với
      // lỗi đăng nhập sai thay vì toast thô.
      setLoginError(t('page.login.expired'));
      return;
    }

    if (notice === SESSION_NOTICE.LOGGED_OUT) {
      // Đăng xuất chủ động KHÔNG phải lỗi → giữ toast "thành công", không nhuộm
      // đỏ thẻ đăng nhập.
      Toast.show({ icon: 'success', content: t('page.login.logged-out') });
    }
    // Chỉ chạy khi vào màn login (một lần) — không phụ thuộc location để tránh
    // consume cờ nhiều lần khi component re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (values) => {
    setLoginError('');

    // POST /api/v1/auth/owner-login → 200 TRẦN { token, expiresAt, displayName, hotelId }.
    // Trim username (autofill hay dính dấu cách; BE cũng Trim). KHÔNG trim mật khẩu —
    // dấu cách có thể là ký tự thật của mật khẩu.
    const { data, error } = await ownerLogin({
      username: (values.username ?? '').trim(),
      password: values.password,
    });

    if (error) {
      // CHỈ 401 là sai tài khoản/mật khẩu (hoặc bị khoá 15' — message BE chứa giờ mở
      // khoá, hiển thị nguyên văn). Mất mạng / 5xx / IIS trả HTML / CORS → báo "không
      // kết nối được máy chủ", KHÔNG báo sai mật khẩu (xem utils/loginError.js).
      // Log để DEV soi trên điện thoại — KHÔNG log username/mật khẩu.
      console.warn('[owner-login] failed', {
        status: error?.status,
        hasMessage: Boolean(getLoginErrorMessage(error)),
      });
      setLoginError(resolveLoginError(error, t));
      return;
    }

    if (!data?.token) {
      // 200 nhưng không có token → không phải sai mật khẩu, báo kèm mã để tra.
      console.warn('[owner-login] failed', {
        status: LOGIN_STATUS_NO_TOKEN,
        hasMessage: false,
      });
      setLoginError(resolveLoginError({ status: LOGIN_STATUS_NO_TOKEN }, t));
      return;
    }

    // Lưu token owner (Bearer cho mọi API :5012) + hạn token + tên hiển thị.
    // `expiresAt` là thứ cho phép tự đăng xuất đúng giờ mà không cần chờ 401.
    saveOwnerSession({
      token: data.token,
      expiresAt: data.expiresAt,
      displayName: data.displayName,
    });

    // Xoá sạch cache RTK Query của phiên trước: đăng nhập bằng tài khoản khác
    // (khách sạn khác) không được thấy số liệu còn sót của chủ cũ.
    dispatch(ownerApiSlice.util.resetApiState());
    dispatch(reportsApiSlice.util.resetApiState());

    navigate(PAGE_URL.OWNER_DASHBOARD, { replace: true });
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <img
            className="login-logo"
            src={diownerLogo}
            alt=""
            aria-hidden="true"
          />
          <div className="login-brand-text">
            <div className="login-brand-name">DiOwner</div>
            <div className="login-tagline">{t('page.login.tagline')}</div>
          </div>
        </div>

        <div className="login-rule" aria-hidden="true" />

        <h1 className="login-title">{t('page.login.title')}</h1>
        <p className="login-subtitle">{t('page.login.subtitle')}</p>

        {loginError ? (
          <div className="login-alert" role="alert">
            <ExclamationCircleOutline className="login-alert-icon" />
            <span className="login-alert-text">{loginError}</span>
          </div>
        ) : null}

        <Form
          layout="vertical"
          className="login-form"
          onFinish={handleLogin}
          // KHÔNG dọn lỗi khi đang gõ (từng làm ở onValuesChange): dải lỗi biến mất
          // giữa lúc nhập làm form nhảy lên ("UI bị chạy"). Lỗi cũ được dọn ở đầu
          // handleLogin — tức lần bấm Đăng nhập kế tiếp.
          footer={
            <Button
              type="submit"
              block
              color="primary"
              size="large"
              loading={isLoading}
              className="login-submit"
            >
              {t('common.button.login')}
            </Button>
          }
        >
          <Form.Item
            name="username"
            label={t('form.login.username.label')}
            rules={[
              {
                required: true,
                message: t('form.login.username.rule.required'),
              },
            ]}
          >
            <LoginField
              type="text"
              clearable
              autoComplete="username"
              placeholder={t('form.login.username.placeholder')}
              prefixIcon={<UserOutline />}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('form.login.password.label')}
            rules={[
              {
                required: true,
                message: t('form.login.password.rule.required'),
              },
            ]}
          >
            <LoginField
              // Con mắt bật → type="text" ⇒ nhìn thấy ký tự thật đang gõ.
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('form.login.password.placeholder')}
              prefixIcon={<LockOutline />}
              suffix={
                <button
                  type="button"
                  className="login-eye"
                  aria-pressed={showPassword}
                  aria-label={
                    showPassword
                      ? t('form.login.password.hide')
                      : t('form.login.password.show')
                  }
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                </button>
              }
            />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Login;
